// src/pages/VitrineHoje.js
//
// Vitrine pública do dia (link.mimodavez.com.br/hoje): tudo que já saiu no
// grupo hoje, pra cliente que não conseguiu acompanhar os posts.
// Lê GET /hoje da function `vitrine` (functions/index.js).
// Filtros num dock de vidro que flutua no rodapé e some ao rolar pra baixo.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const API_URL = (
  process.env.REACT_APP_VITRINE_API_URL ||
  "https://us-central1-achados-67630.cloudfunctions.net/vitrine"
).replace(/\/$/, "");

// /next-group devolve o link do grupo com vaga (mesma rota do site)
const API_PRINCIPAL = "https://us-central1-achados-67630.cloudfunctions.net/api";

const ATUALIZA_MS = 2 * 60 * 1000; // sai post novo ao longo do dia
const NOVO_MS = 60 * 60 * 1000; // selo "novo" na última hora

const FONTES =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,600;1,9..144,600" +
  "&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap";

const TZ = "America/Sao_Paulo";
const fmtHora = new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: TZ });
const fmtData = new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long", timeZone: TZ });
const fmtBRL = (n) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const capitalizar = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const semAcento = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/* ------------------------------------------------------------ categorias */
// Não existe campo de categoria no post: sai do nome do produto.
// A ordem importa (perfume antes de corpo por causa de "body splash").

const CATEGORIAS = [
  { id: "perfume", rotulo: "Perfumes", re: /\b(perfumes?|colonia|body splash|eau de|parfum|fragrancia)\b/ },
  {
    id: "cabelo",
    rotulo: "Cabelo",
    re: /\b(shampoos?|condicionador|capilar|cabelos?|leave.?in|finalizador|chapinha|prancha|secador|babyliss|modelador|tintura|coloracao|escova (secadora|alisadora|rotativa))\b/,
  },
  {
    id: "maquiagem",
    rotulo: "Maquiagem",
    re: /\b(batom|gloss|rimel|cilios|delineador|sombra|paleta|corretivo|blush|iluminador|primer|pinceis|pincel|lapis|contorno|bronzer|maquiagem|po (compacto|solto|facial|translucido)|base (liquida|matte|mate|facial|stick))\b/,
  },
  { id: "unhas", rotulo: "Unhas", re: /\b(esmaltes?|unhas?|manicure|cabine)\b/ },
  {
    id: "pele",
    rotulo: "Skincare",
    re: /\b(serum|protetor solar|filtro solar|fps|skin ?care|facial|vitamina c|acido|retinol|niacinamida|tonico|micelar|olheiras|anti.?idade|antissinais|colageno)\b/,
  },
  {
    id: "corpo",
    rotulo: "Corpo & banho",
    re: /\b(corporal|locao|sabonetes?|esfoliante|desodorante|antitranspirante|banho|body)\b/,
  },
];

const categoriaDe = (nome) => {
  const t = semAcento(nome);
  return (CATEGORIAS.find((c) => c.re.test(t)) || { id: "outros" }).id;
};

/* --------------------------------------------------------------- filtros */

const FAIXAS = [
  { id: "ate30", rotulo: "Até R$ 30", ok: (v) => v <= 30 },
  { id: "30a60", rotulo: "R$ 30 a 60", ok: (v) => v > 30 && v <= 60 },
  { id: "60a100", rotulo: "R$ 60 a 100", ok: (v) => v > 60 && v <= 100 },
  { id: "100mais", rotulo: "Acima de R$ 100", ok: (v) => v > 100 },
];

const ORDENS = [
  { id: "recentes", rotulo: "Mais recentes", cmp: (a, b) => b.ms - a.ms },
  { id: "desconto", rotulo: "Maior desconto", cmp: (a, b) => b.desconto - a.desconto || b.ms - a.ms },
  { id: "menor", rotulo: "Menor preço", cmp: (a, b) => (a.por ?? Infinity) - (b.por ?? Infinity) },
];

const FILTRO_VAZIO = { busca: "", cupom: false, faixa: null, categoria: null, ordem: "recentes" };

function preparar(p) {
  const por = p.priceTo ?? p.priceFrom ?? null;
  const desconto = p.priceFrom && por && p.priceFrom > por ? Math.round((1 - por / p.priceFrom) * 100) : 0;
  return {
    ...p,
    por,
    desconto: desconto >= 5 ? desconto : 0,
    categoria: categoriaDe(p.productName),
    ms: p.publishedAt ? Date.parse(p.publishedAt) || 0 : 0,
    texto: semAcento(`${p.productName} ${p.couponName || ""}`),
  };
}

function aplicar(itens, f) {
  const q = semAcento(f.busca).trim();
  const faixa = FAIXAS.find((x) => x.id === f.faixa);
  const ordem = ORDENS.find((o) => o.id === f.ordem) || ORDENS[0];
  return itens
    .filter(
      (p) =>
        (!q || p.texto.includes(q)) &&
        (!f.cupom || p.couponName) &&
        (!faixa || (p.por != null && faixa.ok(p.por))) &&
        (!f.categoria || p.categoria === f.categoria)
    )
    .sort(ordem.cmp);
}

const contarAtivos = (f) =>
  [f.busca.trim(), f.cupom, f.faixa, f.categoria, f.ordem !== "recentes"].filter(Boolean).length;

// Post único passa pela /oferta/:id (conta o clique); produto de carrossel
// vai direto, porque a /oferta só abre o post inteiro.
const linkDe = (p) => (p.type === "carousel-product" ? p.productUrl : `/oferta/${p.id}`);

async function copiar(texto) {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = texto;
    ta.setAttribute("readonly", "");
    ta.style.cssText = "position:fixed;opacity:0";
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch {}
    ta.remove();
    return ok;
  }
}

async function entrarNoGrupo() {
  try {
    const res = await fetch(`${API_PRINCIPAL}/next-group`);
    const data = await res.json();
    if (data?.invitationLink) {
      window.location.href = data.invitationLink;
      return;
    }
  } catch {}
  window.location.href = "https://mimodavez.com.br";
}

/* ----------------------------------------------------------------- estilo */

const CSS = `
.vt{--pink:#f52b72;--pink-2:#ff5d96;--pink-3:#e90b5a;--ink:#171717;--ink-2:#55555b;
  --muted:#9a9aa0;--line:rgba(23,23,23,.07);--card:#fff;--bg:#fbf7f8;
  --grad:linear-gradient(135deg,var(--pink-2) 0%,var(--pink) 55%,var(--pink-3) 100%);
  min-height:100vh;background:var(--bg);color:var(--ink);position:relative;overflow-x:hidden;
  font-family:"Plus Jakarta Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  padding-bottom:150px;-webkit-tap-highlight-color:transparent}
.vt *{box-sizing:border-box}

.vt-bg{position:fixed;inset:0;pointer-events:none;z-index:0;overflow:hidden}
.vt-bg i{position:absolute;border-radius:50%;filter:blur(64px);opacity:.6;
  animation:vt-drift 18s ease-in-out infinite alternate}
.vt-bg i:nth-child(1){width:360px;height:360px;background:#ffd0e1;top:-110px;left:-110px}
.vt-bg i:nth-child(2){width:300px;height:300px;background:#ffe2ec;top:34%;right:-130px;animation-duration:23s}
.vt-bg i:nth-child(3){width:280px;height:280px;background:#fde6da;bottom:-90px;left:18%;animation-duration:27s}
@keyframes vt-drift{to{transform:translate3d(46px,34px,0) scale(1.1)}}

.vt-wrap{position:relative;z-index:1;max-width:1080px;margin:0 auto;padding:20px 16px 0}
.vt-top{display:flex;align-items:center;justify-content:space-between;gap:12px}
.vt-logo{display:flex;align-items:center;gap:10px;text-decoration:none;color:inherit}
.vt-logo-ic{width:40px;height:40px;border-radius:13px;display:grid;place-items:center;color:#fff;
  background:var(--grad);box-shadow:0 10px 22px rgba(245,43,114,.25);transform:rotate(-3deg)}
.vt-logo-tx{display:flex;flex-direction:column;line-height:1}
.vt-logo-tx b{font-family:Georgia,"Times New Roman",serif;font-style:italic;font-size:21px;color:var(--pink)}
.vt-logo-tx small{font-size:9px;font-weight:800;letter-spacing:3.6px;margin-top:4px}
.vt-upd{font-size:12px;color:var(--muted);font-weight:600}

.vt-hero{margin:30px 0 24px}
.vt-eyebrow{display:inline-flex;align-items:center;gap:8px;font-size:11.5px;font-weight:800;
  letter-spacing:.09em;text-transform:uppercase;color:var(--pink);background:rgba(245,43,114,.09);
  padding:7px 13px;border-radius:999px}
.vt-live{width:7px;height:7px;border-radius:50%;background:var(--pink);animation:vt-ping 1.8s infinite}
@keyframes vt-ping{0%{box-shadow:0 0 0 0 rgba(245,43,114,.55)}70%,100%{box-shadow:0 0 0 9px rgba(245,43,114,0)}}
.vt-hero h1{font-family:Fraunces,Georgia,serif;font-weight:600;font-size:clamp(32px,8vw,52px);
  line-height:1.03;letter-spacing:-.025em;margin:16px 0 10px;max-width:14ch}
.vt-hero h1 em{font-style:italic;color:var(--pink)}
.vt-hero p{margin:0;color:var(--ink-2);font-size:15px;font-weight:500}

.vt-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
@media(min-width:640px){.vt-grid{grid-template-columns:repeat(3,minmax(0,1fr));gap:16px}}
@media(min-width:960px){.vt-grid{grid-template-columns:repeat(4,minmax(0,1fr));gap:18px}}

.vt-card{position:relative;display:flex;flex-direction:column;background:var(--card);border-radius:22px;
  overflow:hidden;border:1px solid var(--line);
  box-shadow:0 1px 2px rgba(23,23,23,.04),0 14px 34px -22px rgba(245,43,114,.35);
  animation:vt-in .55s cubic-bezier(.2,.7,.2,1) both;transition:transform .25s ease,box-shadow .25s ease}
@media(hover:hover){.vt-card:hover{transform:translateY(-4px);
  box-shadow:0 24px 44px -22px rgba(245,43,114,.45)}
  .vt-card:hover .vt-img img{transform:scale(1.05)}}
@keyframes vt-in{from{opacity:0;transform:translateY(16px) scale(.98)}}
.vt-link{display:flex;flex-direction:column;flex:1;color:inherit;text-decoration:none}
.vt-img{position:relative;aspect-ratio:1/1;background:#f6eef1;overflow:hidden}
.vt-img img{width:100%;height:100%;object-fit:cover;display:block;transition:transform .45s ease}
.vt-noimg{position:absolute;inset:0;display:grid;place-items:center;font-size:34px}
.vt-off{position:absolute;top:10px;left:10px;background:var(--ink);color:#fff;font-size:12px;font-weight:800;
  padding:5px 9px;border-radius:999px}
.vt-new{position:absolute;top:10px;right:10px;background:rgba(255,255,255,.86);color:var(--pink);
  -webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);font-size:10.5px;font-weight:800;
  padding:5px 9px;border-radius:999px;text-transform:uppercase;letter-spacing:.07em}
.vt-info{display:flex;flex-direction:column;gap:6px;flex:1;padding:12px 13px 14px}
.vt-time{font-size:11.5px;color:var(--muted);font-weight:600}
.vt-name{margin:0;font-size:13.5px;line-height:1.35;font-weight:600;min-height:2.7em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.vt-price{display:flex;align-items:baseline;flex-wrap:wrap;gap:2px 7px;margin-top:auto}
.vt-price s{font-size:12px;color:var(--muted)}
.vt-price b{font-size:18px;font-weight:800;letter-spacing:-.01em}
.vt-coupon{margin:0 12px 12px;display:flex;align-items:center;justify-content:space-between;gap:8px;
  border:1.5px dashed rgba(245,43,114,.45);background:rgba(245,43,114,.05);color:var(--pink);
  border-radius:13px;padding:8px 10px;font:inherit;font-size:12px;font-weight:700;cursor:pointer;min-width:0}
.vt-coupon > span:first-child{display:flex;align-items:center;gap:6px;min-width:0}
.vt-coupon b{font-weight:800;letter-spacing:.04em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.vt-coupon .cp{font-size:11px;opacity:.75;flex:0 0 auto}

.vt-dock{position:fixed;left:50%;bottom:calc(16px + env(safe-area-inset-bottom));z-index:20;
  width:min(580px,calc(100% - 24px));transform:translateX(-50%);
  transition:transform .5s cubic-bezier(.2,.8,.2,1),opacity .3s ease}
.vt-dock.oculto{transform:translate(-50%,170%);opacity:0}
.vt-dock-inner{display:flex;align-items:center;gap:6px;padding:7px;border-radius:999px;
  background:rgba(255,255,255,.7);-webkit-backdrop-filter:blur(22px) saturate(1.8);
  backdrop-filter:blur(22px) saturate(1.8);border:1px solid rgba(255,255,255,.85);
  box-shadow:0 22px 50px -14px rgba(233,11,90,.38),0 2px 8px rgba(23,23,23,.06);
  animation:vt-float 6s ease-in-out infinite}
@keyframes vt-float{50%{transform:translateY(-5px)}}
.vt-chips{display:flex;gap:4px;flex:1;min-width:0;overflow-x:auto;scrollbar-width:none;
  -webkit-mask-image:linear-gradient(90deg,#000 88%,transparent);mask-image:linear-gradient(90deg,#000 88%,transparent)}
.vt-chips::-webkit-scrollbar{display:none}
.vt-chip{flex:0 0 auto;border:0;background:transparent;color:var(--ink-2);font:inherit;font-size:13px;
  font-weight:700;padding:10px 14px;border-radius:999px;cursor:pointer;white-space:nowrap;
  transition:background .25s,color .25s,transform .15s,box-shadow .25s}
.vt-chip:active,.vt-round:active{transform:scale(.94)}
.vt-chip[aria-pressed="true"]{background:var(--grad);color:#fff;box-shadow:0 8px 18px -8px rgba(245,43,114,.85)}
.vt-round{flex:0 0 auto;position:relative;width:42px;height:42px;border-radius:50%;border:0;
  display:grid;place-items:center;background:var(--ink);color:#fff;cursor:pointer;transition:transform .15s}
.vt-round.claro{background:rgba(23,23,23,.06);color:var(--ink)}
.vt-round.sm{width:36px;height:36px}
.vt-badge{position:absolute;top:-4px;right:-4px;min-width:19px;height:19px;padding:0 5px;border-radius:10px;
  background:var(--pink);color:#fff;font-size:10.5px;font-weight:800;display:grid;place-items:center;
  border:2px solid #fff}
.vt-search{flex:1;min-width:0;border:0;background:transparent;color:var(--ink);font:inherit;
  font-size:16px;padding:9px 6px;outline:none}

.vt-scrim{position:fixed;inset:0;z-index:30;background:rgba(23,23,23,.2);
  -webkit-backdrop-filter:blur(5px);backdrop-filter:blur(5px);animation:vt-fade .25s ease both}
@keyframes vt-fade{from{opacity:0}}
.vt-panel{position:fixed;left:50%;bottom:calc(16px + env(safe-area-inset-bottom));z-index:31;
  width:min(520px,calc(100% - 24px));max-height:82vh;overflow:auto;transform:translateX(-50%);
  background:rgba(255,255,255,.9);-webkit-backdrop-filter:blur(26px) saturate(1.7);
  backdrop-filter:blur(26px) saturate(1.7);border:1px solid rgba(255,255,255,.95);border-radius:30px;
  box-shadow:0 34px 80px -22px rgba(233,11,90,.5);padding:18px 18px 16px;
  animation:vt-pop .4s cubic-bezier(.2,.9,.25,1.15) both}
@keyframes vt-pop{from{opacity:0;transform:translate(-50%,28px) scale(.95)}}
.vt-panel-head{display:flex;align-items:center;justify-content:space-between}
.vt-panel-head b{font-family:Fraunces,Georgia,serif;font-size:22px;font-weight:600}
.vt-panel h4{margin:18px 0 9px;font-size:11.5px;font-weight:800;letter-spacing:.09em;
  text-transform:uppercase;color:var(--muted)}
.vt-opts{display:flex;flex-wrap:wrap;gap:8px}
.vt-opt{border:1.5px solid var(--line);background:#fff;color:var(--ink);font:inherit;font-size:13.5px;
  font-weight:600;padding:9px 14px;border-radius:999px;cursor:pointer;transition:all .2s}
.vt-opt .n{color:var(--muted);font-weight:700;margin-left:4px}
.vt-opt[aria-pressed="true"]{border-color:var(--pink);background:rgba(245,43,114,.08);color:var(--pink)}
.vt-opt[aria-pressed="true"] .n{color:var(--pink)}
.vt-panel-foot{display:flex;gap:10px;margin-top:22px}
.vt-btn{flex:1;border:0;border-radius:999px;padding:14px 18px;font:inherit;font-size:14.5px;font-weight:800;cursor:pointer}
.vt-btn.pri{background:var(--grad);color:#fff;box-shadow:0 12px 26px -10px rgba(245,43,114,.85)}
.vt-btn.sec{flex:0 0 auto;background:rgba(23,23,23,.06);color:var(--ink)}
.vt-btn.inline{flex:0 0 auto;margin-top:18px}

.vt-toast{position:fixed;top:calc(16px + env(safe-area-inset-top));left:50%;z-index:40;
  background:var(--ink);color:#fff;padding:11px 18px;border-radius:999px;font-size:13.5px;font-weight:700;
  box-shadow:0 14px 30px rgba(0,0,0,.2);white-space:nowrap;animation:vt-toast 2.2s ease both}
@keyframes vt-toast{0%{opacity:0;transform:translate(-50%,-14px)}12%,85%{opacity:1;transform:translate(-50%,0)}
  100%{opacity:0;transform:translate(-50%,-8px)}}

.vt-skel .vt-img,.vt-skel-line{background:linear-gradient(90deg,#f3e8ec 0%,#fcf5f7 50%,#f3e8ec 100%);
  background-size:200% 100%;animation:vt-shimmer 1.2s linear infinite}
.vt-skel-line{height:11px;border-radius:6px}
@keyframes vt-shimmer{to{background-position:-200% 0}}

.vt-empty{text-align:center;padding:56px 20px 30px;color:var(--ink-2)}
.vt-empty-ic{font-size:44px;animation:vt-float 4s ease-in-out infinite}
.vt-empty h2{font-family:Fraunces,Georgia,serif;font-weight:600;font-size:26px;color:var(--ink);margin:14px 0 8px}
.vt-empty p{margin:0 auto;max-width:34ch;line-height:1.55}

.vt-foot{text-align:center;margin-top:44px;color:var(--muted);font-size:12.5px}
.vt-join{border:1px solid rgba(245,43,114,.18);background:rgba(255,255,255,.75);color:var(--ink-2);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);font:inherit;font-size:14px;
  padding:13px 20px;border-radius:999px;cursor:pointer;box-shadow:0 10px 26px -16px rgba(245,43,114,.5)}
.vt-join b{color:var(--pink);font-weight:800}
.vt-foot p{margin:18px 0 0}

@media (prefers-reduced-motion:reduce){.vt *,.vt-bg i{animation:none!important;transition:none!important}}
`;

/* ----------------------------------------------------------------- ícones */

const Icone = ({ d, size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor"
       strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d.map((p) => <path key={p} d={p} />)}
  </svg>
);
const IconBusca = () => <Icone d={["M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z", "M20 20l-3.5-3.5"]} />;
const IconFiltro = () => <Icone d={["M4 6h16", "M7 12h10", "M10 18h4"]} />;
const IconX = () => <Icone d={["M6 6l12 12", "M18 6L6 18"]} />;
const IconTag = () => (
  <Icone size={14} d={["M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z", "M7.5 7.5h.01"]} />
);

/* ------------------------------------------------------------- componentes */

function Logo() {
  return (
    <a className="vt-logo" href="https://mimodavez.com.br" aria-label="Mimo da Vez">
      <span className="vt-logo-ic">
        <svg viewBox="0 0 24 24" width="21" height="21" fill="currentColor" aria-hidden="true">
          <path d="M12 21s-6.7-4.35-9.33-8.02C.24 9.66 1.6 5.5 5.3 4.6c2.03-.5 3.9.36 4.98 1.86l.72 1 .72-1c1.08-1.5 2.95-2.36 4.98-1.86 3.7.9 5.06 5.06 2.63 8.38C18.7 16.65 12 21 12 21z" />
        </svg>
      </span>
      <span className="vt-logo-tx"><b>Mimo</b><small>DA VEZ</small></span>
    </a>
  );
}

function Card({ p, i, agora, onCupom }) {
  const novo = p.ms > 0 && agora - p.ms < NOVO_MS;
  return (
    <article className="vt-card" style={{ animationDelay: `${Math.min(i, 12) * 45}ms` }}>
      <a className="vt-link" href={linkDe(p)}>
        <div className="vt-img">
          {p.imageUrl
            ? <img src={p.imageUrl} alt="" loading="lazy" decoding="async" />
            : <span className="vt-noimg" aria-hidden="true">💄</span>}
          {p.desconto > 0 && <span className="vt-off">-{p.desconto}%</span>}
          {novo && <span className="vt-new">novo</span>}
        </div>
        <div className="vt-info">
          {p.ms > 0 && <span className="vt-time">postado às {fmtHora.format(p.ms)}</span>}
          <h3 className="vt-name">{p.productName}</h3>
          {p.por != null && (
            <div className="vt-price">
              {p.desconto > 0 && <s>{fmtBRL(p.priceFrom)}</s>}
              <b>{fmtBRL(p.por)}</b>
            </div>
          )}
        </div>
      </a>
      {p.couponName && (
        <button className="vt-coupon" onClick={() => onCupom(p.couponName)} aria-label={`Copiar cupom ${p.couponName}`}>
          <span><IconTag /><b>{p.couponName}</b></span>
          <span className="cp">copiar</span>
        </button>
      )}
    </article>
  );
}

function Esqueleto() {
  return (
    <div className="vt-grid" aria-label="Carregando">
      {Array.from({ length: 8 }, (_, i) => (
        <div key={i} className="vt-card vt-skel" style={{ animationDelay: `${i * 45}ms` }}>
          <div className="vt-img" />
          <div className="vt-info">
            <div className="vt-skel-line" style={{ width: "40%" }} />
            <div className="vt-skel-line" />
            <div className="vt-skel-line" style={{ width: "55%", height: 16, marginTop: 6 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Aviso({ icone, titulo, texto, botao, onClick }) {
  return (
    <div className="vt-empty">
      <div className="vt-empty-ic" aria-hidden="true">{icone}</div>
      <h2>{titulo}</h2>
      <p>{texto}</p>
      {botao && <button className="vt-btn pri inline" onClick={onClick}>{botao}</button>}
    </div>
  );
}

function Dock({ oculto, filtro, mudar, limpar, ativos, categorias, temCupom, buscando, setBuscando, abrirPainel }) {
  const inputRef = useRef(null);
  useEffect(() => {
    if (buscando) inputRef.current?.focus();
  }, [buscando]);

  const alternar = (campo, valor) => () => mudar({ [campo]: filtro[campo] === valor ? FILTRO_VAZIO[campo] : valor });
  const rapidos = [
    { id: "tudo", rotulo: "Tudo", on: ativos === 0, acao: limpar },
    temCupom && { id: "cupom", rotulo: "Com cupom", on: filtro.cupom, acao: () => mudar({ cupom: !filtro.cupom }) },
    { id: "desconto", rotulo: "Maior desconto", on: filtro.ordem === "desconto", acao: alternar("ordem", "desconto") },
    { id: "ate30", rotulo: "Até R$ 30", on: filtro.faixa === "ate30", acao: alternar("faixa", "ate30") },
    ...categorias.map((c) => ({ id: c.id, rotulo: c.rotulo, on: filtro.categoria === c.id, acao: alternar("categoria", c.id) })),
  ].filter(Boolean);

  return (
    <nav className={`vt-dock${oculto && !buscando ? " oculto" : ""}`} aria-label="Filtros da vitrine">
      <div className="vt-dock-inner">
        {buscando ? (
          <>
            <span className="vt-round claro" aria-hidden="true"><IconBusca /></span>
            <input
              ref={inputRef}
              className="vt-search"
              type="search"
              placeholder="Buscar achadinho…"
              value={filtro.busca}
              onChange={(e) => mudar({ busca: e.target.value })}
              onKeyDown={(e) => (e.key === "Escape" || e.key === "Enter") && setBuscando(false)}
              aria-label="Buscar achadinho"
            />
            <button className="vt-round" onClick={() => { mudar({ busca: "" }); setBuscando(false); }} aria-label="Fechar busca">
              <IconX />
            </button>
          </>
        ) : (
          <>
            <button className="vt-round claro" onClick={() => setBuscando(true)} aria-label="Buscar">
              <IconBusca />
              {filtro.busca.trim() && <span className="vt-badge">1</span>}
            </button>
            <div className="vt-chips">
              {rapidos.map((r) => (
                <button key={r.id} className="vt-chip" aria-pressed={!!r.on} onClick={r.acao}>{r.rotulo}</button>
              ))}
            </div>
            <button className="vt-round" onClick={abrirPainel} aria-label="Todos os filtros">
              <IconFiltro />
              {ativos > 0 && <span className="vt-badge">{ativos}</span>}
            </button>
          </>
        )}
      </div>
    </nav>
  );
}

function Opcao({ on, onClick, children }) {
  return <button className="vt-opt" aria-pressed={!!on} onClick={onClick}>{children}</button>;
}

function Painel({ filtro, mudar, limpar, fechar, categorias, temCupom, total }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && fechar();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [fechar]);

  const alternar = (campo, valor) => () => mudar({ [campo]: filtro[campo] === valor ? null : valor });

  return (
    <>
      <div className="vt-scrim" onClick={fechar} />
      <div className="vt-panel" role="dialog" aria-modal="true" aria-label="Filtrar vitrine">
        <div className="vt-panel-head">
          <b>Filtrar vitrine</b>
          <button className="vt-round claro sm" onClick={fechar} aria-label="Fechar"><IconX /></button>
        </div>

        <h4>Ordenar</h4>
        <div className="vt-opts">
          {ORDENS.map((o) => (
            <Opcao key={o.id} on={filtro.ordem === o.id} onClick={() => mudar({ ordem: o.id })}>{o.rotulo}</Opcao>
          ))}
        </div>

        <h4>Preço</h4>
        <div className="vt-opts">
          {FAIXAS.map((f) => (
            <Opcao key={f.id} on={filtro.faixa === f.id} onClick={alternar("faixa", f.id)}>{f.rotulo}</Opcao>
          ))}
        </div>

        {categorias.length > 0 && (
          <>
            <h4>Categoria</h4>
            <div className="vt-opts">
              {categorias.map((c) => (
                <Opcao key={c.id} on={filtro.categoria === c.id} onClick={alternar("categoria", c.id)}>
                  {c.rotulo}<span className="n">{c.n}</span>
                </Opcao>
              ))}
            </div>
          </>
        )}

        {temCupom && (
          <>
            <h4>Cupom</h4>
            <div className="vt-opts">
              <Opcao on={filtro.cupom} onClick={() => mudar({ cupom: !filtro.cupom })}>Só com cupom</Opcao>
            </div>
          </>
        )}

        <div className="vt-panel-foot">
          <button className="vt-btn sec" onClick={limpar}>Limpar</button>
          <button className="vt-btn pri" onClick={fechar}>
            Ver {total} {total === 1 ? "achadinho" : "achadinhos"}
          </button>
        </div>
      </div>
    </>
  );
}

/* -------------------------------------------------------------- página */

export default function VitrineHoje({ itensIniciais = null }) {
  const [itens, setItens] = useState(itensIniciais && itensIniciais.map(preparar)); // null = carregando
  const [erro, setErro] = useState(false);
  const [filtro, setFiltro] = useState(FILTRO_VAZIO);
  const [painel, setPainel] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [dockOculto, setDockOculto] = useState(false);
  const [toast, setToast] = useState(null);
  const [agora, setAgora] = useState(() => Date.now());

  useEffect(() => {
    document.title = "Vitrine de hoje · Mimo da Vez";
    if (!document.querySelector(`link[href="${FONTES}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = FONTES;
      document.head.appendChild(link);
    }
  }, []);

  const carregar = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/hoje`);
      const data = await res.json();
      if (!res.ok || !data.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setItens((data.products || []).map(preparar).filter((p) => linkDe(p)));
      setErro(false);
      setAgora(Date.now());
    } catch (e) {
      console.error("vitrine /hoje:", e);
      setErro(true);
      setItens((atual) => atual || []);
    }
  }, []);

  useEffect(() => {
    carregar();
    const timer = setInterval(carregar, ATUALIZA_MS);
    const aoVoltar = () => document.visibilityState === "visible" && carregar();
    document.addEventListener("visibilitychange", aoVoltar);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", aoVoltar);
    };
  }, [carregar]);

  // o dock sai de cena rolando pra baixo e volta rolando pra cima
  useEffect(() => {
    let ultimo = window.scrollY;
    let agendado = false;
    const onScroll = () => {
      if (agendado) return;
      agendado = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        if (y > ultimo + 10 && y > 240) setDockOculto(true);
        else if (y < ultimo - 10 || y <= 240) setDockOculto(false);
        ultimo = y;
        agendado = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = painel ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [painel]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  const visiveis = useMemo(() => (itens ? aplicar(itens, filtro) : []), [itens, filtro]);

  const categorias = useMemo(() => {
    const cont = new Map();
    (itens || []).forEach((p) => cont.set(p.categoria, (cont.get(p.categoria) || 0) + 1));
    return CATEGORIAS.filter((c) => cont.get(c.id)).map((c) => ({ ...c, n: cont.get(c.id) }));
  }, [itens]);

  const temCupom = !!itens?.some((p) => p.couponName);
  const ativos = contarAtivos(filtro);
  const total = itens?.length || 0;

  const mudar = useCallback((parcial) => setFiltro((f) => ({ ...f, ...parcial })), []);
  const limpar = useCallback(() => setFiltro(FILTRO_VAZIO), []);
  const fecharPainel = useCallback(() => setPainel(false), []);

  const copiarCupom = async (cupom) => {
    const ok = await copiar(cupom);
    setToast({ id: Date.now(), texto: ok ? `Cupom ${cupom} copiado ✓` : `Cupom: ${cupom}` });
  };

  let conteudo;
  if (itens === null) {
    conteudo = <Esqueleto />;
  } else if (total === 0) {
    conteudo = erro
      ? <Aviso icone="📶" titulo="Não consegui carregar a vitrine" texto="Confere sua internet e tenta de novo." botao="Tentar de novo" onClick={carregar} />
      : <Aviso icone="🌸" titulo="Os achadinhos de hoje ainda vão sair" texto="Os primeiros posts saem logo cedo. Volta daqui a pouco 💕" />;
  } else if (!visiveis.length) {
    conteudo = <Aviso icone="🔎" titulo="Nada com esses filtros" texto="Tenta tirar algum filtro pra ver mais achadinhos." botao="Limpar filtros" onClick={limpar} />;
  } else {
    conteudo = (
      <main className="vt-grid">
        {visiveis.map((p, i) => <Card key={p.id} p={p} i={i} agora={agora} onCupom={copiarCupom} />)}
      </main>
    );
  }

  return (
    <div className="vt">
      <style>{CSS}</style>
      <div className="vt-bg" aria-hidden="true"><i /><i /><i /></div>

      <div className="vt-wrap">
        <header className="vt-top">
          <Logo />
          {itens !== null && !erro && <span className="vt-upd">atualizado às {fmtHora.format(agora)}</span>}
        </header>

        <section className="vt-hero">
          <span className="vt-eyebrow"><i className="vt-live" />Vitrine de hoje</span>
          <h1>Tudo que saiu no grupo <em>hoje</em></h1>
          <p>
            {capitalizar(fmtData.format(agora))}
            {total > 0 && ` · ${total} ${total === 1 ? "achadinho" : "achadinhos"}`}
            {total > 0 && ativos > 0 && ` · mostrando ${visiveis.length}`}
          </p>
        </section>

        {conteudo}

        <footer className="vt-foot">
          <button className="vt-join" onClick={entrarNoGrupo}>
            Quer ver na hora que sai? <b>Entrar no grupo</b>
          </button>
          <p>Mimo da Vez · a gente garimpa, você economiza 💕</p>
        </footer>
      </div>

      {total > 0 && (
        <Dock
          oculto={dockOculto}
          filtro={filtro}
          mudar={mudar}
          limpar={limpar}
          ativos={ativos}
          categorias={categorias}
          temCupom={temCupom}
          buscando={buscando}
          setBuscando={setBuscando}
          abrirPainel={() => setPainel(true)}
        />
      )}

      {painel && (
        <Painel
          filtro={filtro}
          mudar={mudar}
          limpar={limpar}
          fechar={fecharPainel}
          categorias={categorias}
          temCupom={temCupom}
          total={visiveis.length}
        />
      )}

      {toast && <div key={toast.id} className="vt-toast" role="status">{toast.texto}</div>}
    </div>
  );
}
